import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../components/Logo";
import MatrixBackground from "../components/MatrixBackground";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

function roleToPath(role) {
  if (role === "merchant") return "/merchant";
  if (role === "rider") return "/rider";
  if (role === "admin") return "/admin";
  return "/app";
}

const ROLE_DASHBOARDS = ["/merchant", "/rider", "/admin"];

function safeFrom(from, role) {
  if (!from || ROLE_DASHBOARDS.includes(from)) return null;
  return role === "customer" || role === "admin" ? from : null;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) { toast.error(res.error); return; }
    toast.success("Welcome back!");
    const dest = safeFrom(location.state?.from, res.user.role) || roleToPath(res.user.role);
    navigate(dest, { replace: true });
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid-bg noise isolate flex items-center justify-center p-4" data-testid="login-container">
      <MatrixBackground opacity={0.25} />
      <div className="w-full max-w-md fade-in">
        <Link to="/" className="inline-flex items-center gap-1 text-slate-400 hover:text-cyan-300 text-sm mb-6" data-testid="back-home">
          <ArrowLeft size={14} /> Back to home
        </Link>
        <div className="glass rounded-3xl p-7 ring-cyan">
          <div className="flex items-center justify-between">
            <Logo size={34} />
            <div className="text-xs uppercase tracking-widest text-cyan-300 font-bold">Sign in</div>
          </div>
          <h1 className="heading-font text-3xl font-extrabold mt-6">Welcome back.</h1>
          <p className="text-slate-400 text-sm mt-1">Pick up right where you left off.</p>

          <Button
            onClick={googleLogin}
            variant="outline"
            className="w-full mt-6 h-11 rounded-full border-cyan-500/30 bg-white/5 hover:bg-white/10 text-white"
            data-testid="google-login-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.9 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34.4 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
              <path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.8 15.1 19 12 24 12c3 0 5.8 1.1 7.9 2.9l5.7-5.7C34.4 6.1 29.5 4 24 4 16.3 4 9.6 8.4 6.3 14.1z" />
              <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.9-3.1-11.3-7.5l-6.5 5C9.4 39.7 16.1 44 24 44z" />
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2-1.9 3.8-3.5 5.1l6.3 5.2C41 34 44 29.4 44 24c0-1.2-.1-2.3-.4-3.5z" />
            </svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-cyan-500/15" />
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">or email</div>
            <div className="h-px flex-1 bg-cyan-500/15" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                placeholder="you@email.com"
                data-testid="login-email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300 text-xs uppercase tracking-widest font-bold">Password</Label>
                <Link to="/forgot-password" className="text-xs text-cyan-300 hover:underline font-semibold" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-11 bg-white/5 border-cyan-500/15 text-white placeholder:text-slate-500"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <Button type="submit" className="btn-neon w-full h-11 rounded-full" disabled={loading} data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-5 text-sm text-slate-400 text-center">
            New to STACK?{" "}
            <Link to="/register" className="text-cyan-300 font-semibold" data-testid="goto-register">
              Create account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
